import angular from 'angular'
import api, { AbstractCollectionService } from 'components/api'
import moment from 'moment'

class SceneCollectionService extends AbstractCollectionService {
  constructor ($q, $log, ApiService, StateService, toaster) {
    'ngInject'

    super($q, ApiService, StateService, 'SceneCollection', 'scenecollection')

    this._ToastService = toaster
    this.$q = $q
    this.$log = $log
    this.activeSceneCollectionId = null
    ApiService.subscribeToModel('scenecollection_scene', {}, this.sceneCollectionSceneSubscriber.bind(this))
  }

  sceneCollectionSceneSubscriber (error, change) {
    if (!error && change.sceneCollectionId && change.sceneId) {
      this.updatePropertyLocally(change.sceneCollectionId, 'updatedAt', change.updatedAt)
    }
  }

  getSceneCollectionsForScene (sceneId) {
    return this.ApiService.call('scenecollection/getSceneCollections', { sceneId })
  }

  getActiveSceneCollectionId (sceneId) {
    return this.ApiService.call('scene/getActiveSceneCollectionId', { sceneId })
  }

  addInterval (interval) {
    return this.ApiService.call('scenecollection/addInterval', {
      id: interval.id,
      sceneCollectionId: interval.sceneCollectionId,
      interval_start: interval.interval_start,
      interval_end: interval.interval_end,
      priority: interval.priority
    })
  }

  /**
   * Edit an existing interval (wrapper around addInterval with ID)
   * @param {Object} interval - Interval data with id for update
   * @returns {Promise} Update response
   */
  editInterval (interval) {
    return this.addInterval(interval)
  }

  /**
   * Delete an interval by ID
   * @param {number} intervalId - ID of interval to delete
   * @returns {Promise} Delete response
   */
  deleteInterval (intervalId) {
    return this.ApiService.call('interval/deleteInterval', { id: intervalId })
  }

  playNow (sceneCollectionId) {
    return this.ApiService.call('scenecollection/playNow', { sceneCollectionId })
  }

  addSceneCollectionToScene (sceneId, sceneCollectionId) {
    return this.ApiService.call('scenecollection/addSceneCollectionToScene', { sceneId, sceneCollectionId })
  }

  /**
   * Reorder scene collections within a scene via drag-and-drop
   * @param {number} sceneId - The scene ID
   * @param {number} sourceCollectionId - ID of the dragged collection
   * @param {number} targetCollectionId - ID of the drop target collection
   * @returns {Promise} Updated scene collection
   */
  reorderSceneCollections (sceneId, sourceCollectionId, targetCollectionId) {
    return this.ApiService.call('scenecollection/reorderSceneCollections', {
      sceneId,
      sourceCollectionId,
      targetCollectionId
    })
  }

  addSceneCollectionItemToSceneCollection (mediaType, mediaId) {
    if (!this.activeSceneCollectionId) {
      this._ToastService.error('No playlist selected! Please select one by clicking the name on an item in scene content.')
      return this.$q.reject(new Error('No playlist selected! Please select one.'))
    }

    return this.ApiService.call('scenecollectionitem/addSceneCollectionItemToSceneCollection', {
      sceneCollectionId: this.activeSceneCollectionId,
      mediaType,
      mediaId
    })
  }

  setActiveSceneCollection (sceneCollectionId) {
    this.activeSceneCollectionId = sceneCollectionId
  }

  removeActiveSceneCollection () {
    this.activeSceneCollectionId = null
  }

  // ===== CALENDAR-SPECIFIC METHODS =====

  /**
   * Get calendar events for a specific scene within a date range
   * @param {number} sceneId - The scene ID
   * @param {string} startDate - Start date (ISO format)
   * @param {string} endDate - End date (ISO format)
   * @returns {Promise<Array>} Calendar events formatted for FullCalendar
   */
  getCalendarEvents (sceneId, startDate, endDate) {
    return this.ApiService.call('scenecollection/getCalendarEvents', {
      sceneId,
      startDate,
      endDate
    })
  }

  /**
   * Format intervals and scene collections for FullCalendar display
   * @param {Array} sceneCollections - Scene collections with intervals
   * @returns {Array} Formatted events for FullCalendar
   */
  formatIntervalsForCalendar (sceneCollections) {
    const events = []

    // Calculate priority range for dynamic coloring
    const allPriorities = []
    sceneCollections.forEach(sc => {
      if (sc.intervals && sc.intervals.length > 0) {
        sc.intervals.forEach(interval => {
          allPriorities.push(interval.priority || sc.priority || 1)
        })
      }
    })

    const minPriority = allPriorities.length > 0 ? Math.min(...allPriorities) : 1
    const maxPriority = allPriorities.length > 0 ? Math.max(...allPriorities) : 1

    sceneCollections.forEach(sc => {
      if (sc.intervals && sc.intervals.length > 0) {
        sc.intervals.forEach(interval => {
          // Handle zero-duration events by adding 1 hour default duration
          let endTime = interval.end_datetime
          if (interval.start_datetime === interval.end_datetime) {
            const startMoment = moment(interval.start_datetime)
            endTime = startMoment.add(1, 'hour').format('YYYY-MM-DD HH:mm:ss')
          }

          const eventPriority = interval.priority || sc.priority || 1
          const eventColor = this.getDynamicPriorityColor(eventPriority, minPriority, maxPriority)

          // Check if this is the highest priority event (winner)
          const isWinner = eventPriority === maxPriority

          events.push({
            id: `interval_${interval.id}`,
            title: `${sc.name} (P:${eventPriority})${isWinner ? ' 🏆' : ''}`,
            start: interval.start_datetime,
            end: endTime,
            backgroundColor: eventColor,
            borderColor: isWinner ? '#000000' : eventColor,
            borderWidth: isWinner ? 3 : 1,
            textColor: isWinner ? '#ffffff' : '#000000',
            extendedProps: {
              intervalId: interval.id,
              sceneCollectionId: sc.id,
              sceneCollection: sc,
              sceneCollectionName: sc.name,
              priority: eventPriority,
              isWinner,
              intervalStart: interval.start_datetime,
              intervalEnd: interval.end_datetime,
              interval
            }
          })
        })
      }
    })

    return events
  }

  /**
   * Get dynamic color for priority based on current priority range
   * @param {number} priority - Priority level
   * @param {number} minPriority - Minimum priority in current data set
   * @param {number} maxPriority - Maximum priority in current data set
   * @returns {string} HSL color code
   */
  getDynamicPriorityColor (priority, minPriority, maxPriority) {
    // Normalize priority to 0-1 range
    const range = maxPriority - minPriority
    const normalizedPriority = range > 0 ? (priority - minPriority) / range : 0

    // Create color gradient from green (low) to red (high)
    const hue = (1 - normalizedPriority) * 120 // 120 = green, 0 = red
    const saturation = 70
    const lightness = 50

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`
  }

  /**
   * Get color for priority level (legacy - use getDynamicPriorityColor for new code)
   * @param {number} priority - Priority level (1-4+)
   * @returns {string} Hex color code
   */
  getPriorityColor (priority) {
    const colorMap = {
      1: '#28a745', // Low priority - green
      2: '#ffc107', // Medium - yellow
      3: '#fd7e14', // High - orange
      4: '#dc3545' // Critical - red
    }
    return colorMap[Math.min(priority || 1, 4)] || '#6c757d'
  }

  /**
   * Subscribe to calendar-related updates for a specific scene
   * @param {number} sceneId - The scene ID to monitor
   * @param {Function} callback - Callback for interval changes
   * @returns {Function} Unsubscribe function
   */
  subscribeToCalendarUpdates (sceneId, callback) {
    // Subscribe to interval changes
    const intervalUnsubscribe = this.ApiService.subscribeToModel('interval', {}, (error, change) => {
      if (!error) {
        callback(error, { type: 'interval', data: change })
      }
    })

    // Subscribe to scene collection changes
    const sceneCollectionUnsubscribe = this.ApiService.subscribeToModel('scenecollection', {}, (error, change) => {
      if (!error) {
        callback(error, { type: 'sceneCollection', data: change })
      }
    })

    // Return combined unsubscribe function with safety checks
    return () => {
      if (typeof intervalUnsubscribe === 'function') {
        intervalUnsubscribe()
      }
      if (typeof sceneCollectionUnsubscribe === 'function') {
        sceneCollectionUnsubscribe()
      }
    }
  }
}

export default angular.module('scenecollection.service', [
  api
])
  .service('SceneCollectionService', SceneCollectionService).name
